'use client';

import { cn } from '@core/utils';
import dayjs from 'dayjs';
import { memo, useEffect, useState } from 'react';

import { Image } from '@/components/client';
import { WELLO_IMAGE_PREFIX_URL } from '@/constants';

import styles from './WelloOnlySection.module.scss';

const WELLPOINT_LOGOS = [
  `${WELLO_IMAGE_PREFIX_URL}/static/event/localfesta/naver-pay.png`,
  `${WELLO_IMAGE_PREFIX_URL}/static/event/localfesta/kakao-pay.png`,
  `${WELLO_IMAGE_PREFIX_URL}/static/event/localfesta/woowa.png`,
];

const FIVE_MINUTES = 5 * 60 * 1000;

const Timer = memo(function Timer({ isSoldOut }: { isSoldOut: boolean }) {
  const [now, setNow] = useState<dayjs.Dayjs | null>(null);

  useEffect(() => {
    setNow(dayjs());
    const interval = setInterval(() => setNow(dayjs()), 10);

    return () => clearInterval(interval);
  }, []);

  if (!now) {
    return (
      <p className="font-700 tabular-nums text-Gray-white typo-body2">
        <span className="sr-only">남은 시간</span>
        --분 --초 --
      </p>
    );
  }

  // 23시는 기부 불가 시간(23:30~01:00)이라 마감 시 1시간 추가 (1시 오픈)
  const hour = now.hour();
  const extraTime = isSoldOut && hour === 23 ? 60 * 60 * 1000 : 0;
  const timeRemaining = now.endOf('hour').diff(now) + extraTime;
  const isUrgent = timeRemaining <= FIVE_MINUTES;
  const hh = Math.floor(timeRemaining / 3600000);
  const mm = String(Math.floor((timeRemaining % 3600000) / 60000)).padStart(
    2,
    '0',
  );
  const ss = String(Math.floor((timeRemaining % 60000) / 1000)).padStart(
    2,
    '0',
  );
  const cs = String(Math.floor((timeRemaining % 1000) / 10)).padStart(2, '0');

  return (
    <p
      aria-live="polite"
      aria-atomic="true"
      className={cn(
        'font-700 tabular-nums text-Gray-white typo-body2',
        isSoldOut && 'w-full text-center',
        !isSoldOut && styles.timerPulse,
        isUrgent && !isSoldOut && styles.urgent,
      )}
    >
      <span className="sr-only">남은 시간</span>
      {hh > 0 && `${hh}시간 `}
      {mm}분 {ss}초 {cs}
    </p>
  );
});

interface WelloOnlySectionClientProps {
  participantLimit: number;
  rewardCount: number;
}

export const WelloOnlySectionClient = ({
  participantLimit,
  rewardCount,
}: WelloOnlySectionClientProps) => {
  const hour = dayjs().hour();
  const isSoldOut = rewardCount >= participantLimit;
  const progressPercent =
    participantLimit > 0 ? (rewardCount / participantLimit) * 100 : 0;

  return (
    <section
      id="wello-only"
      className="flex w-full scroll-mt-46 flex-col items-center gap-32 bg-R-600 px-20 py-60"
    >
      {/* 상단 이벤트 정보 */}
      <div className="flex w-full max-w-[375px] flex-col items-center gap-20">
        <div className="rounded-xxlarge bg-O-100 px-20 py-6 shadow-md">
          <p className="text-center font-700 text-R-600 typo-body1">Event 1</p>
        </div>

        <div className="flex flex-col items-center gap-8 text-center">
          <p className="font-700 tracking-wider text-O-100 typo-h7">
            TIME REWARD
          </p>
          <div className="flex flex-col items-center">
            <p className="font-800 text-O-100 typo-h4">매시간 정각마다</p>
            <p className="font-800 text-O-100 typo-h4">선착순 10,000원 당첨!</p>
          </div>
        </div>
      </div>

      {/* 프로그레스 카드 */}
      <div
        className={cn(
          'relative flex w-full max-w-[375px] flex-col items-center gap-28 rounded-[36px] bg-Gray-white/10 px-24 py-32 backdrop-blur-sm',
          styles.progressCard,
        )}
      >
        <div className="flex w-full flex-col items-start gap-8">
          <div className="flex w-full flex-col items-start">
            {isSoldOut ? (
              <div className="flex w-full flex-col items-center gap-4">
                <p className="font-700 text-Y-550 typo-body3">
                  {hour}시 선착순 이벤트가 종료됐어요
                </p>
                <p className="font-600 text-Gray-white/80 typo-caption1">
                  다음 시간 오픈까지
                </p>
                <Timer isSoldOut={isSoldOut} />
              </div>
            ) : (
              <>
                <p className="w-full text-right font-600 text-Gray-white/80 typo-caption1">
                  마감까지
                </p>
                <div className="flex w-full items-center justify-between">
                  <p className="font-700 text-Y-550 typo-body2">
                    1만원 받는 중
                  </p>
                  <Timer isSoldOut={isSoldOut} />
                </div>
              </>
            )}
          </div>

          {/* 프로그레스 바 컨테이너 */}
          <div className="relative h-8 w-full">
            {/* 프로그레스 바 */}
            <div
              role="progressbar"
              aria-valuenow={rewardCount}
              aria-valuemin={0}
              aria-valuemax={participantLimit}
              aria-label={`선착순 참여 현황: ${rewardCount}명 / ${participantLimit}명`}
              className="absolute inset-0 overflow-hidden rounded-xxsmall bg-R-800"
            >
              {/* 대기 상태 애니메이션 - 흐르는 빛 효과 */}
              {!isSoldOut && (
                <div className="absolute inset-0 overflow-hidden">
                  <div
                    className={cn(
                      'absolute inset-0 bg-gradient-to-r from-transparent via-Gray-white/20 to-transparent',
                      styles.shimmer,
                    )}
                  />
                </div>
              )}
              {/* 채워진 프로그레스 */}
              <div
                className={cn(
                  'absolute left-0 top-0 h-8 rounded-xxsmall bg-Y-550',
                  'transition-all duration-500 ease-out',
                  !isSoldOut && progressPercent > 0 && styles.progressGlow,
                )}
                style={{ width: `${isSoldOut ? 100 : progressPercent}%` }}
              />
            </div>
            {/* 참여자 수 뱃지 - 프로그레스 바 바깥에 배치 */}
            {!isSoldOut && (
              <div
                className={cn(
                  'absolute top-1/2 -translate-y-1/2 rounded-medium border-2 border-Gray-white bg-Y-550 px-8 py-2 shadow-md transition-all duration-500 ease-out',
                  styles.badge,
                )}
                style={{ left: `max(0px, calc(${progressPercent}% - 24px))` }}
              >
                <p className="text-center font-700 text-Gray-black typo-caption1">
                  {rewardCount}명
                </p>
              </div>
            )}
          </div>

          <div className="flex w-full items-center justify-between font-700 text-Gray-white typo-body4">
            <p>0명</p>
            <p className="text-right">???명</p>
          </div>
        </div>

        {/* 안내 메시지 */}
        <div className="flex w-full items-center justify-center rounded-small bg-Gray-white/20 px-20 py-8 backdrop-blur-sm">
          <div className="flex flex-col items-center text-center">
            <p className="font-700 text-Gray-white typo-caption1">
              매시간 돌아오는 선착순 혜택에 도전해보세요 👀
            </p>
            <p className="font-700 text-Gray-white/90 typo-caption1">
              1시간마다 선착순 1만원 웰포인트를 드려요!
            </p>
          </div>
        </div>

        {/* 자동 참여 안내 */}
        <p className="text-center font-400 text-Gray-white/60 typo-caption1">
          본 이벤트는 <span className="underline">기부시 자동 참여</span>되며
          <br />
          당첨 결과는 기부 완료 시점에 당첨자 대상으로만 안내됩니다.
        </p>
      </div>

      {/* 웰포인트 설명 섹션 */}
      <div className="flex w-full max-w-[375px] flex-col items-center gap-20 overflow-hidden rounded-[36px] bg-Gray-black/10 p-24 backdrop-blur-sm">
        <h3 className="text-center font-700 text-Gray-white typo-h5">
          잠깐! 웰포인트가 뭔가요?
        </h3>
        <p className="text-center font-500 leading-relaxed text-Gray-white/90 typo-body3">
          웰로에서 드리는{' '}
          <span className="font-700 text-Y-550">현금성 포인트</span>에요.
          <br />
          네이버페이, 카카오페이, 배달의민족 등
          <br />
          다양한 쿠폰으로 교환이 가능합니다.
        </p>
        <div className="flex items-center gap-10">
          {WELLPOINT_LOGOS.map((logo, index) => (
            <div
              key={logo}
              className="size-55 shrink-0 overflow-hidden rounded-medium shadow-md transition-transform duration-200 hover:scale-105"
            >
              <Image
                alt={`쿠폰 교환처 ${index + 1}`}
                className="size-full object-cover"
                width={110}
                height={110}
                src={logo}
              />
            </div>
          ))}
        </div>
      </div>

      {/* 이벤트 주의사항 */}
      <div className="flex w-full max-w-[375px] flex-col gap-8 px-8">
        <p className="font-700 text-Gray-white typo-caption1">
          이벤트 주의사항
        </p>
        <ul className="flex flex-col gap-4 font-400 text-Gray-white/70 typo-caption1">
          <li className="ml-18 list-disc">
            해당 이벤트는{' '}
            <span className="underline">매시간 정각마다 선착순 마감</span>
            됩니다.
          </li>
          <li className="ml-18 list-disc">
            이벤트 결과는 기부 완료 시점에 당첨자 대상으로만 안내됩니다.
          </li>
          <li className="ml-18 list-disc">
            이벤트는 기부 건 단위로 증정되므로, 다수의 기부 참여시 중복 지급될
            수 있습니다.
          </li>
        </ul>
      </div>
    </section>
  );
};
